import React, { useState, useEffect, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import PostCalendar from './PostCalendar';
import organicFetch from '../lib/organicFetch';

// Unified cross-platform content calendar for the Social Media Builder. Merges
// Facebook + Instagram organic posts (one `calendar-all` call) with TikTok drafts
// (the tiktok `uploads-list`), color-coded by platform via PostCalendar.

const SITE_URL = 'https://cpx-prototype.netlify.app';
const BRANDS = [{ slug: 'crabplace', name: 'Crab Place' }, { slug: 'crabandcruise', name: 'Crab & Cruise' }];
const ghostBtn = { background: '#fff', color: 'var(--navy)', border: '1px solid var(--gray-200)', borderRadius: 6, fontWeight: 600, fontSize: 12, cursor: 'pointer', fontFamily: 'Montserrat', padding: '6px 12px' };

// A Facebook/Instagram organic_posts row → tag its platform (NULL = legacy FB).
function tagFbIg(p) {
  return { ...p, platform: p.platform === 'instagram' ? 'instagram' : 'facebook' };
}
// A TikTok draft (tiktok_uploads) → calendar shape. TikTok has only created_at +
// a draft status (queued | uploading | in_drafts | failed), so it lands on its
// created day and is never cancelable here (status is never 'scheduled').
function tagTikTok(u) {
  return {
    id: u.id,
    platform: 'tiktok',
    status: u.status,
    caption: u.caption || u.original_name || u.suggested_name || '',
    source_title: u.suggested_name || u.original_name || '',
    created_at: u.created_at,
    media_url: u.r2_thumb_key ? `${SITE_URL}/.netlify/functions/r2-image?key=${encodeURIComponent(u.r2_thumb_key)}` : null,
    created_by: null,
    error: u.error || null,
  };
}

export default function UnifiedCalendar() {
  const [brand, setBrand] = useState('crabplace');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      organicFetch({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'calendar-all', brand }) }).then(r => r.json()).catch(() => ({})),
      fetch('/.netlify/functions/tiktok', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'uploads-list', brand }) }).then(r => r.json()).catch(() => ({})),
    ]).then(([fbig, tt]) => {
      const a = Array.isArray(fbig.posts) ? fbig.posts.map(tagFbIg) : [];
      const b = Array.isArray(tt.uploads) ? tt.uploads.map(tagTikTok) : [];
      setPosts([...a, ...b]);
    }).finally(() => setLoading(false));
  }, [brand]);
  useEffect(() => { load(); }, [load]);

  // FB + IG live in one table (organic_posts) so ids are globally unique and
  // brand-agnostic on 'cancel'. TikTok rows never reach the cancel button (draft
  // statuses are never 'scheduled'), so a numeric id here is always a real post.
  const onCancel = (id) => {
    organicFetch({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'cancel', id, brand }) })
      .then(() => load()).catch(() => {});
  };

  return (
    <div>
      {/* Brand switcher (mirrors the builders) */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, alignItems: 'center' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.3, marginRight: 4 }}>Brand</span>
        {BRANDS.map((b) => (
          <button key={b.slug} onClick={() => setBrand(b.slug)}
            style={{ fontSize: 12, fontWeight: 700, padding: '5px 14px', borderRadius: 16, cursor: 'pointer', fontFamily: 'Montserrat', background: brand === b.slug ? 'var(--navy)' : '#fff', color: brand === b.slug ? '#fff' : 'var(--navy)', border: `1px solid ${brand === b.slug ? 'var(--navy)' : 'var(--gray-200)'}` }}>
            {b.name}
          </button>
        ))}
      </div>

      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Content calendar <span style={{ fontSize: 10.5, fontWeight: 500, color: 'var(--gray-500)' }}>· Facebook, Instagram &amp; TikTok — everyone's posts</span></span>
          <button onClick={load} style={ghostBtn}>Refresh</button>
        </div>
        <div className="card-body">
          {loading && !posts.length
            ? <div style={{ fontSize: 12, color: 'var(--gray-500)', padding: 16 }}><Loader2 size={13} style={{ animation: 'spin 1s linear infinite', verticalAlign: 'middle', marginRight: 6 }} />Loading posts…</div>
            : <PostCalendar posts={posts} onCancel={onCancel} />}
        </div>
      </div>
    </div>
  );
}
